import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { createPortal } from "react-dom";
import { motion } from "motion/react";
import { Icon, type IconName } from "@otrack/ui";

export interface ContextItem {
  label: string;
  icon?: IconName;
  /** A colour square instead of an icon, for Projects. */
  swatch?: string;
  hint?: string;
  tone?: "danger";
  disabled?: boolean;
  /** Opens a second list in place, like "Change project". */
  children?: ContextItem[];
  onSelect?: () => void;
}

const PAD = 10;

/** A menu at the pointer, for right-click and long-press. Arrow keys move; → opens a sub-list. */
export function ContextMenu({ x, y, items, label, onClose }: { x: number; y: number; items: ContextItem[]; label: string; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [stack, setStack] = useState<Array<{ label: string; items: ContextItem[] }>>([]);
  const level = stack.at(-1);
  const list = level?.items ?? items;
  const [active, setActive] = useState(() => list.findIndex((item) => !item.disabled));
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const panel = ref.current;
    if (!panel) return;
    const { width, height } = panel.getBoundingClientRect();
    const left = x + width + PAD > window.innerWidth ? Math.max(PAD, x - width) : x;
    const top = y + height + PAD > window.innerHeight ? Math.max(PAD, window.innerHeight - height - PAD) : y;
    setPosition({ left, top });
  }, [x, y, stack.length]);

  useEffect(() => { itemRefs.current[active]?.focus({ preventScroll: true }); }, [active, stack.length]);

  useEffect(() => {
    const dismiss = (event: Event) => { if (!ref.current?.contains(event.target as Node)) onClose(); };
    const blur = () => onClose();
    document.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("resize", blur);
    window.addEventListener("blur", blur);
    return () => {
      document.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("resize", blur);
      window.removeEventListener("blur", blur);
    };
  }, [onClose]);

  const enabled = list.map((item, index) => (item.disabled ? -1 : index)).filter((index) => index >= 0);
  const step = (delta: number) => {
    const position = enabled.indexOf(active);
    setActive(enabled[(position + delta + enabled.length) % enabled.length] ?? 0);
  };
  const open = (item: ContextItem) => {
    if (item.disabled) return;
    if (item.children) {
      setStack([...stack, { label: item.label, items: item.children }]);
      setActive(Math.max(0, item.children.findIndex((child) => !child.disabled)));
      return;
    }
    onClose();
    item.onSelect?.();
  };
  const back = () => {
    setStack(stack.slice(0, -1));
    setActive(0);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    event.stopPropagation();
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); step(1); break;
      case "ArrowUp": event.preventDefault(); step(-1); break;
      case "Home": event.preventDefault(); setActive(enabled[0] ?? 0); break;
      case "End": event.preventDefault(); setActive(enabled.at(-1) ?? 0); break;
      case "ArrowRight": { const item = list[active]; if (item?.children) { event.preventDefault(); open(item); } break; }
      case "ArrowLeft": if (level) { event.preventDefault(); back(); } break;
      case "Escape": event.preventDefault(); if (level) back(); else onClose(); break;
      case "Tab": event.preventDefault(); onClose(); break;
    }
  };

  return createPortal(<motion.div
    ref={ref}
    role="menu"
    aria-label={level?.label ?? label}
    className="ot-floating ot-menu-panel ot-context"
    data-modal-escape-boundary="true"
    style={{ left: position?.left ?? x, top: position?.top ?? y, visibility: position ? "visible" : "hidden" }}
    initial={{ opacity: 0, scale: 0.96 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ type: "spring", stiffness: 640, damping: 40 }}
    onKeyDown={onKeyDown}
    onContextMenu={(event) => event.preventDefault()}
  >
    <div className="ot-menu">
      {level && <button type="button" className="ot-menu__item ot-context__back" tabIndex={-1} onClick={back}>
        <Icon name="caret-left" className="ot-menu__icon"/><span className="ot-menu__label">{level.label}</span>
      </button>}
      {list.map((item, index) => <button
        key={`${item.label}:${index}`}
        ref={(node) => { itemRefs.current[index] = node; }}
        type="button"
        role="menuitem"
        aria-haspopup={item.children ? "menu" : undefined}
        tabIndex={index === active ? 0 : -1}
        disabled={item.disabled}
        className={`ot-menu__item ${item.tone === "danger" ? "is-danger" : ""} ${index === active ? "is-active" : ""}`}
        onPointerMove={() => { if (!item.disabled && index !== active) setActive(index); }}
        onClick={() => open(item)}
      >
        {item.swatch ? <i className="ot-swatch ot-menu__icon" style={{ background: item.swatch }} aria-hidden="true"/> : item.icon ? <Icon name={item.icon} className="ot-menu__icon"/> : <span className="ot-menu__icon"/>}
        <span className="ot-menu__label">{item.label}</span>
        {item.hint && <span className="ot-menu__hint">{item.hint}</span>}
        {item.children && <Icon name="caret-right" className="ot-context__more"/>}
      </button>)}
    </div>
  </motion.div>, document.body);
}
